import { Box, Container, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import TreffasContainer from "src/components/TreffasContainer";
import { useLocation, useParams } from "react-router-dom";
import IsMounted from "src/components/IsMounted";
import http from "src/utils/http";
import { useTranslation } from "react-i18next";
import PageNotFound from "src/components/PageNotFound";
import LoadingScreen from "src/components/LoadingScreen";
import Page from "src/components/Page";
import Header from "src/views/general/individual-booking/schedule/header";
import PowerdBy from "../../PowerdBy";
import { Users } from "src/context";
import Consultations from "./category/consultations";
import DigitalPackage from "./category/digital-package";
import { useFacebookPixelPublic } from "src/views/hooks/useFacebookPixelPublic";
import { useGoogleAnalyticsPublic } from "src/views/hooks/useGoogleAnalyticsPublic";
import { useEmbedDisplayOptions } from "src/views/hooks/useEmbedDisplayOptions";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const coachCustomPage = process.env.REACT_APP_BLOG_URL;

const OffersList = ({ hideHeader, hideFooter }) => {
  const mounted = IsMounted();
  const [offerList, setOfferList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { personalLink } = useParams();
  const [coachDetails, setCoachDetails] = useState(null);
  const { t } = useTranslation();
  const { mixpanel } = useContext(Users);

  const searchParams = useQuery();
  const display = searchParams.get("display");
  const { embedQueryString } = useEmbedDisplayOptions();

  const [rerenderPixel, setRerenderPixel] = React.useState(1);

  const getOffers = React.useCallback(() => {
    http
      .get("/api/general/individual/coach/offer/list-bypersonal-link", {
        params: {
          personal_link: personalLink,
        },
      })
      .then(({ data }) => {
        if (mounted.current) {
          setOfferList(data);
        }
      })
      .catch((err) => console.log(err.message));
  }, [personalLink, mounted]);

  const getCoachDetails = React.useCallback(() => {
    http
      .get("/api/general/individual/coach/coach-details-bypersonal-link", {
        params: {
          personal_link: personalLink,
        },
      })
      .then(({ data }) => {
        if (mounted.current) {
          if (Object.keys(data).length > 0) {
            setCoachDetails(data);
            setRerenderPixel(2);
          }
        }
      })
      .catch((err) => console.log(err.message))
      .finally(() => setIsLoading(false));
  }, [personalLink, mounted]);

  useEffect(() => {
    mixpanel.track("Coach Packages", {
      location: window.location.href,
    });

    getOffers();
    getCoachDetails();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOffers, getCoachDetails]);

  const user_settings = coachDetails?.settings
    ? JSON.parse(coachDetails?.settings)
    : null;
  const __analytics = user_settings?.analytics ?? null;
  let enable = __analytics?.has_meta_pixel ?? false;
  let pixelId = __analytics?.meta_pixel ?? null;

  const gaId = __analytics?.google_analytics ?? null;
  let googleAnalyticsEnable = __analytics?.has_google_analytics ?? false;

  const trackEventInFacebook = useFacebookPixelPublic({
    track: "ViewContent",
    content_name: "List of packages",
    content_description: "List of created packages.",
    enable: enable,
    pixelId: pixelId,
  });

  const trackEventInGoogleAnalytics = useGoogleAnalyticsPublic({
    enable: googleAnalyticsEnable,
    gaId: gaId,
  });

  React.useEffect(() => {
    trackEventInFacebook();

    trackEventInGoogleAnalytics("view_content", {
      content_name: "List of packages",
      content_description: "List of created packages.",
    });
  }, [rerenderPixel]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (coachDetails === null) {
    return <PageNotFound />;
  }

  const isIframe = window.self !== window.top;

  const consultations = offerList.filter((x) => x.category === "consultation");

  const digital_products = offerList.filter(
    (x) => x.category === "digital-products"
  );

  const courses_package = offerList.filter((x) => x.category === "course");

  return (
    <Page title={`Treffas - ${coachDetails?.name}`}>
      {!hideHeader && (
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            p: { xs: "0px", sm: "40px 10px 32px" },
            cursor: "pointer",
          }}
          onClick={() => {
            window.open(`${coachCustomPage}/page/${personalLink}`, "_blank");
          }}
        >
          <Header coachDetails={coachDetails} />
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: "#FAFAFA",
          minHeight:
            display === "embeded" || isIframe
              ? "inherit"
              : "calc(100vh - 70px)",
          pb: "24px",
        }}
      >
        <Box>
          {consultations.length === 0 &&
          digital_products.length === 0 &&
          courses_package.length === 0 ? (
            <TreffasContainer layout="tcal"
              sx={(themes) => ({
                padding: "0px !important",
              })}
            >
              <Box sx={{ pt: "24px" }}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "24px",
                    color: "#6F6C90",
                    textAlign: "center",
                  }}
                >
                  {t("booking.error.no_offer_found")}
                </Typography>
              </Box>
            </TreffasContainer>
          ) : (
            <Box sx={{ pt: "40px" }}>
              <Box
                sx={{ display: consultations.length === 0 ? "none" : "block" }}
              >
                <TreffasContainer layout="tcal"
                  sx={(themes) => ({
                    padding: "0px !important",
                  })}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "var(--font-grenettepromedium)",
                        lineHeight: "14px",
                        letterSpacing: "0.91px",
                        color: "#6E757C",
                      }}
                    >
                      CONSULTATIONS
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "8px" }}>
                    <Consultations
                      offerList={consultations}
                      coachDetails={coachDetails}
                      display={display}
                      embedQueryString={embedQueryString}
                    />
                  </Box>
                </TreffasContainer>
              </Box>

              <Box
                sx={{
                  display: digital_products.length === 0 ? "none" : "block",
                }}
              >
                <Box sx={{ borderTop: "1px solid #DFE2E6", my: "52px" }}></Box>

                <TreffasContainer layout="tcal"
                  sx={(themes) => ({
                    padding: "0px !important",

                    mt: "28px",
                  })}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "var(--font-grenettepromedium)",
                        lineHeight: "14px",
                        letterSpacing: "0.91px",
                        color: "#6E757C",
                      }}
                    >
                      DIGITAL PRODUCTS
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "8px" }}>
                    <DigitalPackage
                      offerList={digital_products}
                      coachDetails={coachDetails}
                      display={display}
                      embedQueryString={embedQueryString}
                    />
                  </Box>
                </TreffasContainer>
              </Box>

              <Box
                sx={{
                  display: courses_package.length === 0 ? "none" : "block",
                }}
              >
                <Box sx={{ borderTop: "1px solid #DFE2E6", my: "52px" }}></Box>

                <TreffasContainer layout="tcal"
                  sx={(themes) => ({
                    padding: "0px !important",
                    mt: "28px",
                  })}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "var(--font-grenettepromedium)",
                        lineHeight: "14px",
                        letterSpacing: "0.91px",
                        color: "#6E757C",
                      }}
                    >
                      Courses
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "8px" }}>
                    <DigitalPackage
                      offerList={courses_package}
                      coachDetails={coachDetails}
                      display={display}
                      embedQueryString={embedQueryString}
                    />
                  </Box>
                </TreffasContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* powered by */}
      {!hideFooter && (
        <Box>
          <PowerdBy colored={true} />
        </Box>
      )}
    </Page>
  );
};

export default OffersList;
